import { Button, Flex, Form, Radio, RadioChangeEvent } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import { TABLE_FILTER_CONDITION_WITHOUT_VALUE } from '../const';
import { hashString } from '../functions';
import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import { SmxPropsTableContext } from '../providers/propsTableProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';
import {
    FilterConditionType,
    FilterDropdownType,
    FilterType,
    FormFilterConditionType,
    StoreGroupFilterType,
    StructureTableFieldType,
    TableFilterType,
} from '../types';
import { getValueForSavingToServer } from './functions';
import InputForFilters from './InputForFilters';

interface ITableEditForm {
    filterItem: TableFilterType;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    typeField: StructureTableFieldType;
    currentTableFilter: FilterType[];
}

const TableConditionForm: React.FC<ITableEditForm> = ({ currentTableFilter, filterItem, setOpen, typeField }) => {
    const [conditionValue, setConditionValue] = useState<FilterConditionType>('none');
    const { nameQuery, prefixStore } = useContext(SmxPropsTableContext);
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));
    const listGroupFilters = useDynamicSelector('table', 'listGroupFilters');
    const [form] = Form.useForm<FormFilterConditionType>();

    const itemForDropdown = currentTableFilter.map((option) => {
        return {
            key: option.type,
            label: option.type.replaceAll('_', ' '),
            requiredInput: option.requiredInput,
            inputType: option.inputType,
            filterParams: option.filterParams,
        } as FilterDropdownType;
    });

    const getFilterValue = (filter: TableFilterType, conditionValue: FilterConditionType) => {
        switch (typeField) {
            case 'Date':
                if (conditionValue === 'between') {
                    if (filter.value) {
                        if (filter.value.toString().includes('..')) {
                            const [value1, value2] = filter.value.toString().split('..');
                            return [dayjs(value1), dayjs(value2)];
                        }
                        return [dayjs(filter.value), dayjs(filter.value)];
                    }
                    return [dayjs(), dayjs()];
                }

                if (filter.value) {
                    if (filter.value.toString().includes('..')) {
                        const [value1] = filter.value.toString().split('..');
                        return dayjs(value1);
                    }
                    return dayjs(filter.value);
                }

                return dayjs();
            case 'Int':
                return filter.value || 1;
            default:
                return filter.value;
        }
    };

    useEffect(() => {
        let keyField = conditionValue;
        if (filterItem.condition !== 'none') {
            setConditionValue(filterItem.condition);
            keyField = filterItem.condition;
        }

        form.setFieldsValue({
            [keyField]: getFilterValue(filterItem, conditionValue),
            [keyField + 1]: getFilterValue(filterItem, conditionValue),
            [keyField + 2]: getFilterValue(filterItem, conditionValue),
        });
    }, [filterItem, form, conditionValue]);

    useEffect(() => {
        if (currentTableFilter.length && filterItem.condition === 'none') {
            setConditionValue(currentTableFilter[0].type);
        }
    }, [currentTableFilter]);

    const setDataToStore = (condition: string, value: string, filterDone: boolean) => {
        setterTable({
            listGroupFilters: {
                ...listGroupFilters,
                [filterItem.parentKey]: {
                    ...listGroupFilters[filterItem.parentKey],
                    listFilters: {
                        ...listGroupFilters[filterItem.parentKey].listFilters,
                        [filterItem.key]: {
                            ...filterItem,
                            condition: condition,
                            value: value,
                            filterDone: filterDone,
                        },
                    },
                },
            } as StoreGroupFilterType,
        });
    };

    const onChangeRadio = (e: RadioChangeEvent) => {
        const newConditionValue = e.target.value.replace(' ', '_');
        setConditionValue(newConditionValue);

        setDataToStore(newConditionValue, `${filterItem.value}`, false);
    };

    const getValueFromForm = (values: FormFilterConditionType, typeField?: StructureTableFieldType) => {
        if (conditionValue === 'between') {
            switch (typeField) {
                case 'Date': {
                    const value1 = getValueForSavingToServer(values[conditionValue][0], typeField);
                    const value2 = getValueForSavingToServer(values[conditionValue][1], typeField);
                    return `${value1}..${value2}`;
                }
                case 'Int': {
                    const value1 = getValueForSavingToServer(values[`${conditionValue}1`], typeField);
                    const value2 = getValueForSavingToServer(values[`${conditionValue}2`], typeField);
                    return `${value1}..${value2}`;
                }
                default:
                    return getValueForSavingToServer(values[conditionValue], typeField);
            }
        }
        return getValueForSavingToServer(values[conditionValue], typeField);
    };

    const onFinish = (values: FormFilterConditionType) => {
        setOpen(false);

        const isConditionWithoutValue = TABLE_FILTER_CONDITION_WITHOUT_VALUE.has(conditionValue);

        setDataToStore(
            conditionValue,
            isConditionWithoutValue ? ' ' : getValueFromForm(values, filterItem.option.typeField),
            true,
        );
    };

    return (
        <Form
            form={form}
            name={`conditionForm${hashString(JSON.stringify(filterItem))}`}
            style={{ width: '100%' }}
            onFinish={onFinish}
            layout="vertical"
        >
            <Radio.Group
                value={conditionValue}
                onChange={onChangeRadio}
                style={{ display: 'flex', flexDirection: 'column', gap: 5 }}
            >
                {itemForDropdown.map((item) => (
                    <div key={item.key} style={{ width: '100%' }}>
                        <Radio value={item.key}>{item.label}</Radio>
                        {item.requiredInput && conditionValue === item.key && (
                            <Flex style={{ margin: '5px 0 5px 25px', width: '100%' }}>
                                <InputForFilters item={item} />
                            </Flex>
                        )}
                    </div>
                ))}
            </Radio.Group>
            <Flex justify={'end'} style={{ marginTop: 20 }}>
                <Button htmlType="submit" type="primary" size={'middle'}>
                    Done
                </Button>
            </Flex>
        </Form>
    );
};

export default TableConditionForm;
